import { Fragment, type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { QuickViewProvider } from 'dibs-search-product-tile/exports/QuickViewProvider';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import { SbRespPlaceholderTile } from '../../finding/SbRespSearchResultContainer/SbRespPlaceholderTile';
import { SbSharedProductAdWrapper } from '../../finding/SbSharedProductAdWrapper/SbSharedProductAdWrapper';
import { useSharedUrgencySignals } from '../../finding/hooks/sharedUrgencySignals/useSharedUrgencySignals';
import { useItemShippingPrices } from '../../finding/hooks/useItemShippingPrices';
import { useSbSharedItemTracking } from '../../finding/hooks/sharedItemTracking/useSbSharedItemTracking';
import { usePartialItems } from '../../finding/hooks/usePartialItems';
import { useSbSelector } from '../../reducers/useSbSelector';
import { SbSharedPartialItemsPlaceholder } from '../../finding/SbSharedPartialItemsPlaceholder/SbSharedPartialItemsPlaceholder';
import { useOneBuyPageTemplateAbTestHelper } from '../helpers/useOneBuyPageTemplateAbTestHelper';

import {
    VERTICAL_OFFSET_SB_RESP_IMG,
    VERTICAL_OFFSET_MOBILE_IMG,
} from '../../constants/imageConstants';
import { locations } from '../../utils/tracking/shared/favoritesTracking';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';

import { type FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';
import { type BuySharedResultContainer_viewer$key } from './__generated__/BuySharedResultContainer_viewer.graphql';
import { type BuySharedResultContainer_buyPage$key } from './__generated__/BuySharedResultContainer_buyPage.graphql';
import { type BuySharedResultContainer_user$key } from './__generated__/BuySharedResultContainer_user.graphql';

import classnames from 'classnames';
import styles from './BuySharedResultContainer.scss';

const LAZY_LOAD_ITEM_ROWS = 3;

const viewerFragment = graphql`
    fragment BuySharedResultContainer_viewer on Viewer {
        ...SharedItemTile_viewer
        ...useSbSharedItemTracking_viewer
    }
`;
const buyPageFragment = graphql`
    fragment BuySharedResultContainer_buyPage on ItemSearchQueryConnection {
        ...SharedItemTile_itemSearch
        ...SbSharedProductAdWrapper_itemSearch
        edges {
            node {
                item {
                    serviceId
                    ...usePartialItems_item
                    ...FavoritesProvider_item
                    ...useSharedUrgencySignals_item
                    ...useItemShippingPrices_item
                    ...useSbSharedItemTracking_item
                    ...SharedItemTile_item
                        @arguments(
                            isTrade: $isTrade
                            fetchVideo: $fetchTileVideo
                            priceBookName: $priceBookName
                            showSeller: $showSeller
                        )
                }
            }
        }
        ...useSbSharedItemTracking_itemSearch
        ...useOneBuyPageTemplateAbTestHelper_buyPage
    }
`;
const userFragment = graphql`
    fragment BuySharedResultContainer_user on User {
        ...SharedItemTile_user
    }
`;

type Props = {
    viewer: BuySharedResultContainer_viewer$key;
    buyPage: BuySharedResultContainer_buyPage$key;
    user: BuySharedResultContainer_user$key;
    showBuyPageRedesign: boolean;
};

export const BuySharedResultContainer: FC<Props> = ({
    viewer: viewerRef,
    buyPage: buyPageRef,
    user: userRef,
    showBuyPageRedesign = false,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const buyPage = useFragment(buyPageFragment, buyPageRef);
    const user = useFragment(userFragment, userRef);
    const items = (buyPage?.edges || []).map(edge => edge?.node?.item).filter(filterFalsy);
    const { fetchPartialItems, partialItemsIds, numOfInflatedItems, numOfPlaceholderItems } =
        usePartialItems(items);
    const inflatedItems = partialItemsIds.length ? items.slice(0, numOfInflatedItems) : items;

    const { fetchItemUrgencySignals, getUrgencySignal } = useSharedUrgencySignals(items);
    const { fetchItemShippingPrices, getItemShippingPrices, getItemShippingTrackingData } =
        useItemShippingPrices(items);
    const { fireItemImpressionTracking, fireItemClickTracking } = useSbSharedItemTracking({
        viewer,
        itemSearch: buyPage,
        items,
        getUrgencySignal,
        getItemShippingPrices,
        getItemShippingTrackingData,
    });
    const showMeasurements = useSbSelector(state => state.displayPreferences.showMeasurements);
    const refetchInFlight = useSbSelector(state => state.filters.refetchInFlight);
    const { isClient, userId } = useSbSelector(state => state.relayVariables.variables);
    const { isMobile, isBannerAdsAbTestVariant, isItemTileRedesignTestVariant } =
        useServerVarsContext();
    const { shouldShowRefineMenu } = useOneBuyPageTemplateAbTestHelper({ buyPage });

    let itemsPerRow = showBuyPageRedesign ? 4 : 3;
    if (itemsPerRow > 3 && shouldShowRefineMenu) {
        // if refine menu is shown we mustn't show more than 3 items per row due to spacing
        itemsPerRow = 3;
    }
    const gridContainerClassName = classnames(styles.gridContainer, {
        [styles.gridContainer2ItemsRow]: isMobile,
        [styles.gridContainer4ItemsRow]: !isMobile && itemsPerRow === 4,
        [styles.gridContainerAdaptiveItemsRow]: !isMobile && itemsPerRow !== 4,
    });

    return (
        <div className={isItemTileRedesignTestVariant ? gridContainerClassName : styles.container}>
            <FavoritesProvider
                disable={!isClient}
                items={items}
                // location should be kept the same GROWTH-4297
                location={locations.SEARCH_BROWSE}
                userId={userId}
                fetchFolder={!isMobile}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <QuickViewProvider>
                        <>
                            {inflatedItems.map((item, index) => {
                                if (refetchInFlight && !isMobile) {
                                    return <SbRespPlaceholderTile key={item?.serviceId} />;
                                }
                                return (
                                    <Fragment key={item?.serviceId}>
                                        {!showBuyPageRedesign && isBannerAdsAbTestVariant && (
                                            <SbSharedProductAdWrapper
                                                index={index}
                                                itemSearch={buyPage}
                                                pageCol={isMobile ? 2 : 3}
                                            />
                                        )}
                                        <SharedItemTile
                                            showCreatorName
                                            showSellerName={false}
                                            showMeasurements={
                                                showMeasurements || showBuyPageRedesign
                                            }
                                            key={item?.serviceId}
                                            item={item}
                                            user={user}
                                            favorites={favorites}
                                            index={index}
                                            lazyLoadStartIndex={
                                                isMobile
                                                    ? undefined
                                                    : LAZY_LOAD_ITEM_ROWS * itemsPerRow
                                            }
                                            imageLoadVerticalOffset={
                                                isMobile
                                                    ? VERTICAL_OFFSET_MOBILE_IMG
                                                    : VERTICAL_OFFSET_SB_RESP_IMG
                                            }
                                            beforeRender={() => {
                                                fetchItemUrgencySignals(index);
                                                fetchItemShippingPrices(index);
                                            }}
                                            onContentVisible={() =>
                                                fireItemImpressionTracking({
                                                    itemId: item?.serviceId,
                                                    index,
                                                })
                                            }
                                            onClick={() =>
                                                fireItemClickTracking({
                                                    itemId: item?.serviceId,
                                                    index,
                                                })
                                            }
                                            customClassName={classnames(styles.tileWidth, {
                                                [styles.desktop4ItemsRow]:
                                                    !isMobile && itemsPerRow === 4,
                                                [styles.desktop3ItemsRow]:
                                                    !isMobile && itemsPerRow === 3,
                                            })}
                                            viewer={viewer}
                                            itemSearch={buyPage}
                                            fetchFolderOnMouseOver
                                            urgencySignal={
                                                getUrgencySignal(item.serviceId)?.message
                                            }
                                            shippingPrices={getItemShippingPrices(item.serviceId)}
                                        />
                                    </Fragment>
                                );
                            })}
                            <SbSharedPartialItemsPlaceholder
                                numOfPlaceholderItems={numOfPlaceholderItems}
                                fetchPartialItems={fetchPartialItems}
                                numOfInflatedItems={numOfInflatedItems}
                                numOfAboveFoldRows={LAZY_LOAD_ITEM_ROWS}
                                itemsPerRow={!isMobile ? itemsPerRow : undefined}
                            />
                        </>
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
};
